import { useLazyGetEnergyDataSpecificQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { usePostEnergyInitiativeMutation } from '@/api/recalcV2/endpoints/energyInitiativesApiSlice';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EnergySumChartInteractive from '../highcharts/EnergySumChartInteractive';

import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';
import DateRangeWithGenerate from './DateRangeWithGenerate';
import ToolMenu from './InitiativeGraphTool';

// * i18n
import { useTranslation } from 'react-i18next';

// * Redux
import TimestampSummaryIndicator from '@/components/indicators/TimestampSummaryIndicator';
import { showSnackbar } from '@/redux/snackbarSlice';
import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';
import { useDispatch } from 'react-redux';

const ZONE = 'Europe/Copenhagen';
const fmt = (ms) =>
  typeof ms === 'number'
    ? DateTime.fromMillis(ms, { zone: ZONE }).toLocaleString(
        DateTime.DATETIME_SHORT,
      )
    : '—';

const asOption = (n) => ({
  id: n?.energyTreeNodeId ?? n?.id ?? n,
  type: n?.type,
  label:
    n?.name ?? n?.displayName ?? n?.title ?? String(n?.energyTreeNodeId ?? n),
});

const toUtcIso = (v) => {
  if (v == null) return null;
  if (v && typeof v === 'object' && typeof v.toMillis === 'function') {
    return DateTime.fromMillis(v.toMillis(), { zone: 'utc' }).toISO();
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return DateTime.fromMillis(v, { zone: 'utc' }).toISO();
  }
  if (typeof v === 'string') {
    const d = DateTime.fromISO(v, { zone: 'utc' });
    return d.isValid ? d.toISO() : null;
  }
  return null;
};

function deriveState(ini) {
  if (
    ini.baselineStart &&
    ini.baselineEnd &&
    ini.comparisonStart &&
    ini.comparisonEnd
  )
    return 'Done';
  if (ini.baselineStart && ini.baselineEnd && ini.comparisonStart)
    return 'Active';
  if (ini.baselineStart && ini.baselineEnd) return 'Initialized';
  if (ini.baselineStart) return 'Initialized';
  return 'Created';
}

const steps = [
  'Navn & beskrivelse',
  'Vælg datakilder',
  'Vælg baseline periode',
  'Vælg sammenlignings periode',
];

const CreateInitiativeWithPlotDialog = ({ open, onClose, energyGroup }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const [postEnergyInitiative, { isLoading }] =
    usePostEnergyInitiativeMutation();

  const [formState, setFormState] = React.useState({
    name: '',
    description: '',
    state: null,
    completed: null,
    baselineStart: null,
    baselineEnd: null,
    comparisonStart: null,
    comparisonEnd: null,
    preferredModel: 0,
    energyTreeNodes: [],
    energyGroupId: energyGroup?.energyGroupId || null,
  });

  const [activeStep, setActiveStep] = useState(0);

  const [sourceSelectionMode, setNodeSelectionMode] = React.useState('node');

  const resetAll = useCallback(() => {
    setFormState({
      name: '',
      description: '',
      state: null,
      completed: null,
      baselineStart: null,
      baselineEnd: null,
      comparisonStart: null,
      comparisonEnd: null,
      preferredModel: 0,
      energyTreeNodes: [],
      energyGroupId: energyGroup?.energyGroupId || null,
    });
    setNodeSelectionMode('node');
    setSelectedNodes([]);
    const nowLocal = DateTime.now().setZone(ZONE);
    setStartDt(nowLocal.minus({ days: 7 }).startOf('day'));
    setEndDt(nowLocal.endOf('day'));
    setActiveTool(null);
    setSelections({ baseline: {}, comparison: {} });
    setActiveStep(0);
  }, [energyGroup?.energyGroupId]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      resetAll();
      onClose();
    }
  }, [isLoading, resetAll, onClose]);

  const handleCreateInitiative = React.useCallback(async () => {
    const payload = {
      ...formState,
      baselineStart: toUtcIso(formState.baselineStart),
      baselineEnd: toUtcIso(formState.baselineEnd),
      comparisonStart: toUtcIso(formState.comparisonStart),
      comparisonEnd: toUtcIso(formState.comparisonEnd),
      state: deriveState(formState),
    };

    try {
      await postEnergyInitiative({
        accountId: energyGroup.accountId,
        energyInitiative: payload,
      }).unwrap();
      dispatch(
        showSnackbar({ message: 'Initiativ oprettet', severity: 'success' }),
      );
      handleClose();
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under oprettelse af initiativ',
          severity: 'error',
          error: error?.data?.message || error.error || 'Unknown error',
        }),
      );
    }
  }, [
    formState,
    postEnergyInitiative,
    energyGroup.accountId,
    dispatch,
    handleClose,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const nodeGroupsByType = React.useMemo(() => {
    const map = new Map();
    energyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        const group = map.get(type);
        if (group) {
          group.push(node);
        }
      }
    });
    return map;
  }, [energyGroup]);

  const seuTypes = React.useMemo(
    () => Array.from(nodeGroupsByType.keys()),
    [nodeGroupsByType],
  );

  // --- Node options from the energy group ---
  const nodeOptions = useMemo(() => {
    const src = energyGroup?.energyTreeNodes ?? [];
    return src.map(asOption).filter((o) => o.id);
  }, [energyGroup]);

  const [selectedNodes, setSelectedNodes] = useState([]);
  const [useManualInputs, setUseManualInputs] = useState(false);

  // --- Luxon time range (defaults: last 7 days in Copenhagen time) ---
  const now = DateTime.now().setZone(ZONE);
  const [startDt, setStartDt] = useState(
    now.minus({ weeks: 4 }).startOf('day'),
  );
  const [endDt, setEndDt] = useState(now.endOf('day'));

  const startMs = startDt?.toMillis();
  const endMs = endDt?.toMillis();
  const isRangeValid =
    typeof startMs === 'number' && typeof endMs === 'number' && endMs > startMs;

  // --- Lazy query for energy data ---
  const [fetchEnergyData, { data, isFetching, isError, error }] =
    useLazyGetEnergyDataSpecificQuery();

  const timestampSummary = data?.EnergySummary?.TimestampsSummary || null;
  const timeRange = useMemo(() => {
    return {
      start: startMs,
      end: endMs,
    };
  }, [startMs, endMs]);

  const validation = getTimestampSummaryValidationStatus(
    timeRange,
    timestampSummary,
  );

  useEffect(() => {
    if (validation.isOutOfBounds) {
      setStartDt(validation.adjustedRange[0]);
      setEndDt(validation.adjustedRange[1]);
      dispatch(
        showSnackbar({
          message:
            'Tidsintervallet er justeret grundet manglende måling udenfor den valgte periode.',
          severity: 'info',
        }),
      );
    }
  }, [dispatch, validation]);

  const handleGenerate = () => {
    const node_ids = selectedNodes.map((n) => n.id);
    if (!energyGroup?.energyGroupId || node_ids.length === 0 || !isRangeValid)
      return;

    fetchEnergyData({
      accountId: energyGroup?.accountId,
      energyGroupId: energyGroup?.energyGroupId,
      start: startMs,
      end: endMs,
      node_ids,
    });
  };

  const pointsForChart = useMemo(
    () => (Array.isArray(data?.EnergyData) ? data.EnergyData : []),
    [data],
  );

  // Interactive selection tools (baseline / comparison)
  const [activeTool, setActiveTool] =
    useState(null); /* 'baseline' | 'comparison' | null */
  const [selections, setSelections] = useState({
    baseline: {},
    comparison: {},
  });

  useEffect(() => {
    if (activeStep === 2) {
      setActiveTool('baseline-point');
    } else if (activeStep === 3) {
      setActiveTool('comparison-point');
    } else {
      setActiveTool(null);
    }
  }, [activeStep]);

  const clearBaseline = () => setSelections((s) => ({ ...s, baseline: {} }));
  const clearComparison = () =>
    setSelections((s) => ({ ...s, comparison: {} }));
  const clearAll = () => setSelections({ baseline: {}, comparison: {} });

  const canGenerate =
    energyGroup?.energyGroupId && selectedNodes.length > 0 && isRangeValid;

  // useEffect(() => {
  //   setFormState((prev) => ({
  //     ...prev,
  //     baselineStart: selections.baseline.start || null,
  //     baselineEnd: selections.baseline.end || null,
  //     comparisonStart: selections.comparison.start || null,
  //     comparisonEnd: selections.comparison.end || null,
  //   }));
  // }, [selections]);

  // Reflect selections back to form state
  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      baselineStart: selections.baseline.start
        ? DateTime.fromMillis(selections.baseline.start, { zone: ZONE })
        : null,
      baselineEnd: selections.baseline.end
        ? DateTime.fromMillis(selections.baseline.end, { zone: ZONE })
        : null,
      comparisonStart: selections.comparison.start
        ? DateTime.fromMillis(selections.comparison.start, { zone: ZONE })
        : null,
      comparisonEnd: selections.comparison.end
        ? DateTime.fromMillis(selections.comparison.end, { zone: ZONE })
        : null,
    }));
  }, [selections]);

  // ------- Step validation -------
  const step1Valid = Boolean(formState.name?.trim()); // description optional
  const step2Valid = formState.energyTreeNodes.length > 0;
  // const step3Valid =
  // step3 (periods) is optional for creation in your current logic, so we won’t block “Create”
  // But we will still require a valid chart range before fetching data:
  const isBaselineSet =
    formState.baselineStart !== null && formState.baselineEnd !== null;
  const step3RangeHint = !isRangeValid;

  // ------- Step content renderers -------
  const renderStep1 = () => (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <TextField
        label={t('add_initiative_form.name.label')}
        name="name"
        variant="filled"
        size="small"
        required
        error={!formState.name}
        value={formState.name}
        onChange={handleInputChange}
        fullWidth
        sx={{
          '& .MuiFilledInput-root': {
            '&:before': { borderBottom: 'none' },
            '&:after': { borderBottom: 'none' },
            '&:hover:before': { borderBottom: 'none !important' },
            borderRadius: 1,
          },
        }}
      />
      <TextField
        label={t('add_initiative_form.description.label')}
        name="description"
        variant="filled"
        size="small"
        value={formState.description}
        onChange={handleInputChange}
        fullWidth
        multiline
        sx={{
          mb: 2,
          '& .MuiFilledInput-root': {
            '&:before': { borderBottom: 'none' },
            '&:after': { borderBottom: 'none' },
            '&:hover:before': { borderBottom: 'none !important' },
            borderRadius: 1,
          },
        }}
      />
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={2}>
      <ToggleButtonGroup
        value={sourceSelectionMode}
        fullWidth
        size="small"
        exclusive
        onChange={(_, newMode) => {
          if (newMode !== null) setNodeSelectionMode(newMode);
        }}
        sx={{ alignSelf: 'flex-start', mr: 1 }}
      >
        <ToggleButton value="node">
          <ListItemText primary="Individuelle noder" />
        </ToggleButton>
        <ToggleButton value="seu">
          <ListItemText primary="SEU" />
        </ToggleButton>
      </ToggleButtonGroup>

      {sourceSelectionMode === 'node' && (
        <Autocomplete
          multiple
          options={nodeOptions}
          size="small"
          value={selectedNodes}
          onChange={(_, val) => {
            setSelectedNodes(val);
            setFormState((prev) => ({
              ...prev,
              energyTreeNodes: val.map((v) => v.id),
            }));
          }}
          disableCloseOnSelect
          filterSelectedOptions
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          sx={{ flex: 2, minWidth: 260 }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box
                sx={{
                  backgroundColor: getTypeColor(option?.type),
                  position: 'relative',
                  borderRadius: '25px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `1px solid divider`,
                  marginRight: 1,
                }}
              >
                <i
                  style={{ color: 'white' }}
                  className={`fa-solid ${getTypeIcon(option?.type)}`}
                />
              </Box>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('button_labels.select_nodes')}
              helperText={t('button_labels.select_nodes_helper_text')}
              variant="filled"
              fullWidth
              required
              error={!formState.energyTreeNodes.length}
              disableUnderline
              sx={{
                '& .MuiFilledInput-root': {
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:before': { borderBottom: 'none !important' },
                  borderRadius: 1,
                  '& .MuiFilledInput-input': { margin: '18px 18px' },
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.label}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: getTypeColor(option.type),
                  color: 'white',
                  borderRadius: 2,
                }}
              />
            ))
          }
        />
      )}

      {sourceSelectionMode === 'seu' && (
        <Autocomplete
          multiple
          options={seuTypes}
          getOptionLabel={(type) => type} // keep it simple for label logic
          value={Array.from(
            new Set(
              formState.energyTreeNodes
                .map((id) =>
                  energyGroup.energyTreeNodes.find(
                    (n) => n.energyTreeNodeId === id,
                  ),
                )
                .filter(Boolean)
                .map((node) => node?.type)
                .filter((type) => type != null),
            ),
          )}
          onChange={(_, newSelectedTypes) => {
            const selectedIds = newSelectedTypes
              .flatMap((type) => nodeGroupsByType.get(type) || [])
              .map((node) => node.energyTreeNodeId);

            setSelectedNodes(
              nodeOptions.filter((o) => selectedIds.includes(o.id)),
            );
            setFormState((prev) => ({ ...prev, energyTreeNodes: selectedIds }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('button_labels.select_seu')}
              helperText={t('button_labels.select_seu_helper_text')}
              variant="filled"
              placeholder={t('button_labels.select_seu')}
              required
              fullWidth
              error={!formState.energyTreeNodes.length}
              sx={{
                '& .MuiFilledInput-root': {
                  '&:before': { borderBottom: 'none' },
                  '&:after': { borderBottom: 'none' },
                  '&:hover:before': { borderBottom: 'none !important' },
                  borderRadius: 1,
                  '& .MuiFilledInput-input': { margin: '14px 14px' },
                },
              }}
            />
          )}
          renderOption={(props, type) => {
            const allOfType = nodeGroupsByType.get(type) || [];
            const selectedCount = allOfType.filter((node) =>
              formState.energyTreeNodes.includes(node.energyTreeNodeId),
            ).length;

            return (
              <li {...props} key={type}>
                <Box
                  sx={{
                    backgroundColor: getTypeColor(type),
                    position: 'relative',
                    borderRadius: '25px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `1px solid divider`,
                    marginRight: 1,
                  }}
                >
                  <i
                    style={{ color: 'white' }}
                    className={`fa-solid ${getTypeIcon(type)}`}
                  />
                </Box>
                <Typography sx={{ flexGrow: 1 }}>
                  {type} ({selectedCount}/{allOfType.length})
                </Typography>
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((type, index) => {
              const allOfType = nodeGroupsByType.get(type) || [];
              const selectedCount = allOfType.filter((node) =>
                formState.energyTreeNodes.includes(node.energyTreeNodeId),
              ).length;
              const percentage = Math.round(
                (selectedCount / allOfType.length) * 100,
              );
              const color = getTypeColor(type);

              return (
                <Box
                  key={type}
                  sx={{ position: 'relative', display: 'inline-block', mr: 1 }}
                >
                  <Chip
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <i
                          className={`fa-solid ${getTypeIcon(type)}`}
                          style={{ color: 'white' }}
                        />
                        <Typography>
                          {type} {selectedCount}/{allOfType.length}
                        </Typography>
                      </Box>
                    }
                    color={
                      selectedCount === allOfType.length ? 'success' : 'warning'
                    }
                    variant="filled"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: color,
                      color: 'white',
                    }}
                    {...getTagProps({ index })}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      left: 0,
                      right: 0,
                      height: 3,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          selectedCount === allOfType.length
                            ? '#2e7d32'
                            : '#ed6c02',
                      },
                    }}
                  />
                </Box>
              );
            })
          }
        />
      )}
    </Stack>
  );

  const renderStep3 = () => (
    <Stack spacing={1}>
      {step3RangeHint && (
        <Typography variant="caption" color="error">
          Please pick a valid time range (End must be after Start).
        </Typography>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={useManualInputs}
            onChange={() => setUseManualInputs(!useManualInputs)}
          />
        }
        label="Brug manuelle input felter"
      />
      {useManualInputs && (
        <>
          <Typography variant="subtitle2" color="textSecondary">
            Angiv baseline periode
          </Typography>
          <DateRangePicker
            startText={t('add_initiative_form.baseline_period.start_label')}
            endText={t('add_initiative_form.baseline_period.end_label')}
            value={[formState.baselineStart, formState.baselineEnd]}
            variant="filled"
            onChange={(newValue) => {
              setFormState((prev) => ({
                ...prev,
                baselineStart: newValue[0],
                baselineEnd: newValue[1],
              }));
            }}
            renderInput={(startProps, endProps) => (
              <>
                <TextField
                  {...startProps}
                  variant="outlined"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 1,
                    },
                  }}
                  fullWidth
                />
                <TextField
                  {...endProps}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 1,
                    },
                  }}
                  fullWidth
                />
              </>
            )}
          />
        </>
      )}

      {!useManualInputs && (
        <Card variant="outlined" sx={{ p: 2, minHeight: 420 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Chip
              variant="filled"
              label={`Baselineperiode: ${fmt(selections.baseline.start)} → ${fmt(selections.baseline.end)}`}
              onDelete={clearBaseline}
              sx={{
                height: 'auto',
                borderRadius: 2,
                color: 'info.main',
                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1),
              }}
            />
            <DateRangeWithGenerate
              startDt={startDt}
              endDt={endDt}
              canGenerate={canGenerate}
              onChangeRange={(newStart, newEnd) => {
                setStartDt(newStart.setZone(ZONE));
                setEndDt(newEnd.setZone(ZONE));
              }}
              onGenerate={(startMs, endMs) => {
                const node_ids = selectedNodes.map((n) => n.id);
                if (!energyGroup?.energyGroupId || node_ids.length === 0)
                  return;

                fetchEnergyData({
                  accountId: energyGroup?.accountId,
                  energyGroupId: energyGroup?.energyGroupId,
                  start: startMs,
                  end: endMs,
                  node_ids,
                });
              }}
              zone={ZONE}
              locale="da"
              generateLabel="Generer graf"
            />
          </Stack>

          {!data && !isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
              spacing={1}
            >
              <Typography variant="body1">
                Efter du har valgt datakilder for initiativet, skal du klikke på{' '}
                <strong>Generer graf</strong> som findes ved at åbne
                datavælgeren oppe til højre, og en tidsperiode som initiatives
                foretages.
              </Typography>
              {!energyGroup?.energyTreeNodes?.length && (
                <Typography variant="body2">
                  Der er ikke fundet nogen noder i denne energigruppe.
                </Typography>
              )}
              {activeStep === 2 || activeStep === 3 ? (
                <Typography variant="caption" color="text.secondary">
                  Tip: Du kan også bruge de manuelle input felter ved at skifte
                  "Brug manuelle input felter" til.
                </Typography>
              ) : null}
            </Stack>
          )}

          {isFetching && (
            <div
              style={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading…
            </div>
          )}

          {isError && (
            <div
              style={{
                height: 400,
                color: '#b00020',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            >
              Failed to load data
              {error?.data ? `: ${JSON.stringify(error.data)}` : ''}
            </div>
          )}

          {!isFetching &&
            !isError &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length > 0 && (
              <EnergySumChartInteractive
                points={pointsForChart}
                selections={selections}
                activeTool={activeTool}
                onChange={setSelections}
                title=""
                height={380}
              />
            )}

          {!isFetching &&
            !isError &&
            data &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length === 0 && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: 400, opacity: 0.7 }}
              >
                <Typography>
                  No data returned for the chosen nodes and period.
                </Typography>
              </Stack>
            )}

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent={'center'}
          >
            <ToolMenu
              activeTool={activeTool}
              activeStep={activeStep}
              disabled={!canGenerate}
              setActiveTool={setActiveTool}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );

  const renderStep4 = () => (
    <Stack spacing={1}>
      {step3RangeHint && (
        <Typography variant="caption" color="error">
          Please pick a valid time range (End must be after Start).
        </Typography>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={useManualInputs}
            onChange={() => setUseManualInputs(!useManualInputs)}
          />
        }
        label="Brug manuelle input felter"
      />
      {useManualInputs && (
        <>
          <Typography variant="subtitle2" color="textSecondary">
            Angiv sammenlignings periode
          </Typography>
          <DateRangePicker
            startText="Start"
            endText="Slut"
            value={[formState.comparisonStart, formState.comparisonEnd]}
            onChange={(newValue) => {
              setFormState((prev) => ({
                ...prev,
                comparisonStart: newValue[0],
                comparisonEnd: newValue[1],
              }));
            }}
            renderInput={(startProps, endProps) => (
              <>
                <TextField
                  {...startProps}
                  variant="filled"
                  helperText="Sammenligningsperioden skal ligge efter baseline perioden"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 2,
                    },
                  }}
                  fullWidth
                />
                <TextField
                  {...endProps}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      '&:before': { borderBottom: 'none' }, // Removes default underline
                      '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                      '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                      borderRadius: 1,
                      flexGrow: 2,
                    },
                  }}
                  fullWidth
                />
              </>
            )}
          />
        </>
      )}

      {!useManualInputs && (
        <Card variant="outlined" sx={{ p: 2, minHeight: 420 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Chip
              variant="filled"
              onDelete={clearComparison}
              label={`Sammenligningsperiode: ${fmt(selections.comparison.start)} → ${fmt(selections.comparison.end)}`}
              sx={{
                height: 'auto',
                borderRadius: 2,
                color: 'success.main',
                backgroundColor: (theme) =>
                  alpha(theme.palette.success.main, 0.1),
              }}
            />
            <TimestampSummaryIndicator
              globalTimeRange={{
                start: startDt ? startDt.toMillis() : null,
                end: endDt ? endDt.toMillis() : null,
              }}
              timestampSummary={timestampSummary}
            />
            <DateRangeWithGenerate
              startDt={startDt}
              endDt={endDt}
              canGenerate={canGenerate}
              onChangeRange={(newStart, newEnd) => {
                setStartDt(newStart.setZone(ZONE));
                setEndDt(newEnd.setZone(ZONE));
              }}
              onGenerate={() => {
                handleGenerate();
              }}
              zone={ZONE}
              locale="da"
              generateLabel="Generer graf"
              timestampSummary={timestampSummary}
            />
          </Stack>

          {!data && !isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
              spacing={1}
            >
              <Typography variant="body1">
                Efter du har valgt datakilder for initiativet, skal du klikke på{' '}
                Generer graf som findes ved at åbne datovælgeren oppe til højre,
                og en tidsperiode som initiatives foretages.
              </Typography>
              {!energyGroup?.energyTreeNodes?.length && (
                <Typography variant="body2">
                  Der er ikke fundet nogen noder i denne energigruppe.
                </Typography>
              )}
              {activeStep === 2 || activeStep === 3 ? (
                <Typography variant="caption" color="text.secondary">
                  Tip: Du kan også bruge de manuelle input felter ved at skifte
                  "Brug manuelle input felter" til.
                </Typography>
              ) : null}
            </Stack>
          )}

          {isFetching && (
            <div
              style={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Loading…
            </div>
          )}

          {isError && (
            <div
              style={{
                height: 400,
                color: '#b00020',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            >
              Failed to load data
              {error?.data ? `: ${JSON.stringify(error.data)}` : ''}
            </div>
          )}

          {!isFetching &&
            !isError &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length > 0 && (
              <EnergySumChartInteractive
                points={pointsForChart}
                selections={selections}
                activeTool={activeTool}
                onChange={setSelections}
                title=""
                height={380}
              />
            )}

          {!isFetching &&
            !isError &&
            data &&
            Array.isArray(pointsForChart) &&
            pointsForChart.length === 0 && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: 400, opacity: 0.7 }}
              >
                <Typography>
                  No data returned for the chosen nodes and period.
                </Typography>
              </Stack>
            )}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent={'center'}
          >
            <ToolMenu
              activeTool={activeTool}
              activeStep={activeStep}
              disabled={!canGenerate}
              setActiveTool={setActiveTool}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );

  // ------- Navigation handlers -------
  const handleNext = () => {
    if (activeStep === 0 && !step1Valid) return;
    if (activeStep === 1 && !step2Valid) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={
        (activeStep === 2 && !useManualInputs) ||
        (activeStep === 3 && !useManualInputs)
          ? 'xl'
          : 'md'
      }
    >
      <DialogTitle>Opret initiativ</DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Stack spacing={2}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepIcon-root.Mui-completed': {
                        color: 'success.main',
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: 'text.secondary',
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && renderStep1()}
            {activeStep === 1 && renderStep2()}
            {activeStep === 2 && renderStep3()}
            {activeStep === 3 && renderStep4()}
            {activeStep === 2 || activeStep === 3 ? (
              <Typography variant="caption" color="text.secondary">
                Tip: Brug værktøjsmenuen under grafen til at vælge og justere
                baseline- og sammenligningsperioder. Du kan også bruge de
                manuelle input felter ved at skifte "Brug manuelle input felter"
                til.
              </Typography>
            ) : null}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Button onClick={handleClose}>Afbryd</Button>
        </Box>
        <Box>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ mr: 1 }}
          >
            Tilbage
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !step1Valid) ||
                (activeStep === 1 && !step2Valid) ||
                ((activeStep === 2 || activeStep === 3) && !isBaselineSet)
              }
            >
              Næste
            </Button>
          )}

          {(activeStep === steps.length - 1 || activeStep === 2) && (
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              onClick={handleCreateInitiative}
            >
              Opret initiativ
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInitiativeWithPlotDialog;
